<template>
    <v-row no-gutters>
        <v-row no-gutters class="d-flex justify-end">
            <v-col cols="2" v-if="!actions">
                <v-btn-primary block @click="upsertCompany()" size="large">{{
                    company.id ? "Modifier" : "Créer"
                }}</v-btn-primary>
            </v-col>
            <v-col cols="12">
                <v-form v-model="thirdPartyCompanyValid">
                    <v-row no-gutters class="pt-1">
                        <v-col class="d-flex pb-2 pr-2" cols="6">
                            <v-text-field-filled
                                v-model="company.name"
                                type="text"
                                :label="$t('user.companyName')"
                                :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                required
                                outlined
                                :readonly="readonly"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                        <v-col class="d-flex pb-2 pr-2" cols="6">
                            <v-text-field-filled
                                v-model="company.shortName"
                                type="text"
                                :label="$t('settings.commercialName')"
                                outlined
                                :readonly="readonly"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="4" class="d-flex pb-2 pr-2">
                            <v-text-field-filled
                                v-model="company.siret"
                                outlined
                                label="SIRET"
                                @input="company.siret = truncateInput(company.siret, 14)"
                                :readonly="readonly"
                                data-cy="editCompany-adress"
                                hide-details="auto"
                                @change="onSiretChange"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="4" class="d-flex pb-2 pr-2">
                            <v-text-field-filled
                                v-model="company.siren"
                                outlined
                                label="SIREN"
                                readonly
                                data-cy="editCompany-adress"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="4" class="d-flex pb-2 pr-2">
                            <v-text-field-filled
                                v-model="company.vatNumber"
                                outlined
                                label="Num TVA"
                                :readonly="readonly"
                                data-cy="editCompany-adress"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="d-flex pb-2 pr-2" cols="12">
                            <v-text-field-filled
                                v-model="company.address"
                                type="text"
                                :label="$t('settings.address') + ' *'"
                                :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                required
                                outlined
                                :readonly="readonly"
                                data-cy="editCompany-adress"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="d-flex pb-2 pr-2" cols="6">
                            <v-text-field-filled
                                v-model="company.postalCode"
                                type="text"
                                :label="$t('settings.postalCode') + ' *'"
                                :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                required
                                outlined
                                :readonly="readonly"
                                data-cy="editCompany-postalCode"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                        <v-col class="d-flex pb-2 pr-2" cols="6">
                            <v-text-field-filled
                                v-model="company.city"
                                type="text"
                                :label="$t('settings.city') + ' *'"
                                :rules="[(v) => !!v || $t('settings.itemRequired')]"
                                required
                                outlined
                                :readonly="readonly"
                                data-cy="editCompany-city"
                                hide-details="auto"
                                density="compact"
                            />
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="pl-2" cols="8">
                            <v-radio-group v-model="company.vatOption" color="primary" inline>
                                Régime de TVA :
                                <v-radio
                                    v-for="option in availableVatOptions"
                                    :key="option.value"
                                    class="pl-2"
                                    :label="option.text"
                                    :value="option.value"
                                />
                            </v-radio-group>
                        </v-col>
                    </v-row>
                </v-form>
            </v-col>
        </v-row>
    </v-row>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import gql from "graphql-tag";
import UPSERT_THIRDPARTY_COMPANY from "../graphql/company/UpsertThirdPartyCompany.gql";
import { Component, Prop, Vue, toNative } from "vue-facing-decorator";
import { truncateInput } from "@/helpers/formInput.helper";
@Component({
    components: {},
    emits: ["upsert"],
})
export class EditThirdPartyCompany extends Vue {
    @Prop({ default: "" }) readonly inThirdPartyCompanyId: string;
    @Prop({ default: "" }) readonly parentCompanyId: string;
    @Prop({ default: null }) actions: any;

    thirdPartyCompanyValid: boolean = false;

    availableVatOptions: Array<any> = [
        { value: null, text: "N/C" },
        { text: "Débits", value: "Debits" },
        { text: "Encaissements", value: "Receipts" },
    ];

    thirdPartyCompanyId_: string = "";

    get thirdPartyCompanyId() {
        if (this.thirdPartyCompanyId_) {
            return this.thirdPartyCompanyId_;
        }
        return this.inThirdPartyCompanyId;
    }

    set thirdPartyCompanyId(value) {
        this.thirdPartyCompanyId_ = value;
    }

    async beforeMount() {
        if (this.actions && this.actions.action) {
            this.actions.action.function = this.upsertCompany;
            this.actions.action.display = true;
            this.actions.action.loading = false;
            this.actions.action.disabled = false;
            this.actions.action.title = this.thirdPartyCompanyId ? "Modifier" : "Créer";
        }
    }

    async upsertCompany() {
        const result = await this.$apollo.mutate({
            mutation: UPSERT_THIRDPARTY_COMPANY,
            variables: {
                parentCompanyId: this.parentCompanyId,
                thirdPartyCompanyId: this.thirdPartyCompanyId || "",
                name: this.company.name,
                shortName: this.company.shortName,
                address: this.company.address,
                postalCode: this.company.postalCode,
                city: this.company.city,
                country: this.company.country,
                vatOption: this.company.vatOption,
                siret: `${this.company.siret}_tcpOf_${this.parentCompanyId}`,
                siren: this.company.siren,
                vatNumber: this.company.vatNumber,
            },
        });

        this.thirdPartyCompanyId = result.data.upsertThirdPartyCompany.id;

        this.$emit("upsert", this.company);
    }

    @Apollo({
        query: gql`
            query companyDetails($id: ID!) {
                company(where: { id: $id }) {
                    id
                    name
                    shortName
                    parentCompany {
                        id
                    }
                    address
                    postalCode
                    city
                    country
                    vatOption
                    siret
                    siren
                    vatNumber
                }
            }
        `,
        variables() {
            return {
                id: this.thirdPartyCompanyId,
            };
        },
        skip() {
            return !this.thirdPartyCompanyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.company) {
                    this.company = JSON.parse(JSON.stringify(data.company));
                    if (this.company.siret) {
                        this.company.siret = this.company.siret.split("_")[0];
                    }
                } else {
                    this.company = JSON.parse(JSON.stringify(this.defaultCompany));
                }
            }
        },
    })
    company: any = {
        id: "",
        name: "",
        shortName: "",
        parentCompany: {
            id: "",
        },
        siret: "",
        siren: "",
        address: "",
        postalCode: "",
        city: "",
        country: "",
        vatOption: "",
    };

    get readonly() {
        return false;
    }

    defaultCompany: any = {
        id: "",
        name: "",
        shortName: "",
        parentCompany: {
            id: "",
        },
        address: "",
        postalCode: "",
        city: "",
        country: "",
        vatOption: "",
    };

    onSiretChange(value) {
        if (this.company.siret) {
            this.company.siren = this.company.siret.substring(0, 9);
        }
    }
}

export default toNative(EditThirdPartyCompany);
</script>
